import GithubSlugger from 'github-slugger';
import Link from 'next/link';
import { AlertWarning, Information } from 'plaid-threads/Icons';
import type { FC, ReactElement, ReactNode } from 'react';
import styled from 'threads5/styles/styled';
export type { Props } from 'plaid-threads/Callout';

const StyledCallout = styled('div')(({ theme }) => ({
  border: '1px solid #767676',
  borderRadius: '2px',
  display: 'flex',
  padding: '16px',
}));

const StyledIcon = styled('div')({
  color: '#111',
  flexShrink: 0,
  marginRight: '8px',
});

const StyledContent = styled('div')({});

const StyledTitle = styled('div')({
  position: 'relative',
  fontWeight: 700,
  color: '#111',
  margin: 0,
  fontFamily: "'Plaid Sans', Helvetica, Arial, sans-serif",
  '&:hover': {
    cursor: 'pointer',
  },
});

const StyledOffsetAnchor = styled('span')({
  position: 'absolute',
  top: '-96px',
});

const StyledChildren = styled('div')({
  'p:last-child': {
    marginBottom: 0,
  },
});

/**
 * Custom legacy Threads-esque callout component.
 *
 * Rebuilt to use a `<div>` tag instead of `<p>` for content, allowing consuming
 * components to nest `<p>` tags without creating invalid markup.
 *
 * @see [redacted]################################################################################
 */
const Callout: FC<{
  className?: string;
  children?: ReactNode;
  icon?: ReactElement;
  title?: string;
  warning?: boolean;
}> = ({ className, children, title, icon, warning }) => {
  const defaultIcon = warning ? <AlertWarning /> : <Information />;
  const id = title ? GithubSlugger.slug(title) : undefined;

  return (
    <StyledCallout className={className}>
      <StyledIcon>{icon ?? defaultIcon}</StyledIcon>
      <StyledContent>
        {title && (
          <Link href={`#${id}`}>
            <StyledTitle>
              {title}
              <StyledOffsetAnchor id={id} data-algolia='linkedHeadingAnchor' />
            </StyledTitle>
          </Link>
        )}
        <StyledChildren>{children}</StyledChildren>
      </StyledContent>
    </StyledCallout>
  );
};

export default Callout;
